<template>
  <AppPanelShell
    storage-key="aradcore-patient"
    :brand-subtitle="me?.siteTitle || 'پورتال بیمار'"
    brand-icon="i-lucide-heart-pulse"
    :nav-items="navItems"
    :search-groups="[]"
    :hide-search="true"
    :hide-navbar-user-menu="true"
    :breadcrumbs="breadcrumbs"
    :tab-bar-items="tabItems"
    :flush="inVisitRoom"
  >
    <template #navbar-actions>
      <PatientMemberSwitcher
        v-if="me?.account.members.length && me.account.members.length > 1"
        :current-id="me.patient.id"
        :members="me.account.members"
      />
      <UButton
        v-if="!inVisitRoom"
        color="primary"
        variant="soft"
        icon="i-lucide-home"
        to="/"
      >
        بازگشت به سایت
      </UButton>
    </template>
    <template #user-menu="{ collapsed }">
      <PatientAccountFooter
        v-if="me?.patient"
        :patient="me.patient"
        :collapsed="collapsed"
        @logout="logout"
      />
    </template>
    <slot />
  </AppPanelShell>
</template>

<script setup lang="ts">
import type { PatientPortalHome } from '~/types/patient'

const route = useRoute()
const { data } = await useFetch<{ item: PatientPortalHome }>('/api/patient/me')
const me = computed(() => data.value?.item)

const inVisitRoom = computed(() => /\/patient\/visits\/[^/]+$/.test(route.path))

const { enabledKeys } = useSiteModules()
const navItems = computed(() => {
  const items = [
    { label: 'خانه', icon: 'i-lucide-house', to: '/patient', active: route.path === '/patient' },
    { label: 'نوبت‌های من', icon: 'i-lucide-calendar-days', to: '/patient/appointments', active: route.path.startsWith('/patient/appointments') },
    { label: 'سوابق سلامت', icon: 'i-lucide-folder-heart', to: '/patient/health', active: route.path.startsWith('/patient/health') || route.path.startsWith('/patient/record') || route.path.startsWith('/patient/forms') },
    { label: 'گفت‌وگو', icon: 'i-lucide-messages-square', to: '/patient/chat', active: route.path.startsWith('/patient/chat') },
    { label: 'ویزیت مجازی', icon: 'i-lucide-video', to: '/patient/visits', active: route.path.startsWith('/patient/visits') },
    { label: 'امتیاز و معرفی', icon: 'i-lucide-sparkles', to: '/patient/points', active: route.path.startsWith('/patient/points') || route.path.startsWith('/patient/referral') },
    { label: 'کارت هدیه', icon: 'i-lucide-gift', to: '/patient/gifts', active: route.path.startsWith('/patient/gifts') },
    { label: 'پکیج جلسات', icon: 'i-lucide-layers', to: '/patient/packages', active: route.path.startsWith('/patient/packages') },
    { label: 'حساب کاربری', icon: 'i-lucide-user-round', to: '/patient/profile', active: route.path.startsWith('/patient/profile') },
  ]
  if (!enabledKeys.value.has('servicePackages')) {
    return [items.filter(item => item.to !== '/patient/packages')]
  }
  return [items]
})

const tabItems = computed(() => inVisitRoom.value
  ? []
  : [
      { label: 'خانه', icon: 'i-lucide-house', to: '/patient' },
      { label: 'نوبت‌های من', icon: 'i-lucide-calendar-days', to: '/patient/appointments' },
      { label: 'سوابق سلامت', icon: 'i-lucide-folder-heart', to: '/patient/health' },
      { label: 'حساب کاربری', icon: 'i-lucide-user-round', to: '/patient/profile' },
    ])

const breadcrumbs = computed(() => {
  const crumbs: Array<{ label: string; to?: string }> = [
    { label: 'پورتال بیمار', to: '/patient' },
  ]
  if (route.path.startsWith('/patient/appointments')) crumbs.push({ label: 'نوبت‌های من' })
  else if (route.path.startsWith('/patient/health') || route.path.startsWith('/patient/forms') || route.path.startsWith('/patient/record')) crumbs.push({ label: 'سوابق سلامت' })
  else if (route.path.startsWith('/patient/chat')) crumbs.push({ label: 'گفت‌وگو' })
  else if (route.path.startsWith('/patient/visits')) crumbs.push({ label: 'ویزیت مجازی' })
  else if (route.path.startsWith('/patient/points')) crumbs.push({ label: 'امتیاز وفاداری' })
  else if (route.path.startsWith('/patient/referral')) crumbs.push({ label: 'معرفی دوست' })
  else if (route.path.startsWith('/patient/gifts')) crumbs.push({ label: 'کارت هدیه' })
  else if (route.path.startsWith('/patient/packages')) crumbs.push({ label: 'پکیج جلسات' })
  else if (route.path.startsWith('/patient/profile')) crumbs.push({ label: 'حساب کاربری' })
  return crumbs
})

async function logout() {
  await $fetch('/api/patient/auth/logout', { method: 'POST' })
  await navigateTo('/patient/login')
}
</script>
